'use client';
import { MSProductWithFullDetailWithVaraintPreset } from '@/util/query/product-query';
import { toPriceString } from '@/util/toPriceString';
import { CUSTOMER_LEVEL } from '@prisma/client';
import dynamic from 'next/dynamic';
import { memo, useEffect } from 'react';
// import { CustomTooltip } from "../ui/Tooltip/custom-tooltip";
import { useTranslation } from '@/app/i18n/client';
import CollectionListItemClient from '@/components/collections/collection-list-favorite-client';
import ProductTagRenderer from '@/components/product-tag-renderer';
import SmartImage from '@/components/SmartImage';
import ColorStyleContext from '@/lib/providers/color-style-privider';
import { applyDiscountPromotion } from '@/util/applyPromotion';
import {
    getBestPriceByType,
    getPromotionDiscountedPrice,
    StepPriceType,
} from '@/util/promotion';
import { formatCurrencyFormat } from '@/util/util';
import { useContext } from 'react';
import './product-card.css';

interface CollectionListItemProps {
    product: MSProductWithFullDetailWithVaraintPreset;
    href: string;
    // t: TFunction<"translation", undefined>;
    level: CUSTOMER_LEVEL;
    refreshfav?: boolean;
    variantId?: string;
    priorityImage?: boolean;
    shopCurrency: string;
    favoriteEnable: boolean;
}

// Helper component to render currency smaller than the numeric price.
// shopCurrency format example: "฿ {price}" or "{price} ฿" (always contains "{price}").
// We split around {price} and render currency parts with smaller font-size.
interface CurrencyPriceProps {
    priceString: string; // already formatted numeric string (e.g., 230 or 1,250.00)
    shopCurrency: string; // pattern with {price}
    wrapperClassName?: string; // classes applied to outer wrapper
    priceClassName?: string; // classes for the numeric part
    currencyClassName?: string; // classes for currency part (defaults to smaller text)
    id?: string;
}

const CurrencyPrice = ({
    priceString,
    shopCurrency,
    wrapperClassName = '',
    priceClassName = '',
    currencyClassName = 'text-sm',
    id,
}: CurrencyPriceProps) => {
    const parts = shopCurrency.split('{price}');
    const prefix = parts[0] ? parts[0].trim() : '';
    const suffix = parts[1] ? parts[1].trim() : '';
    return (
        <span
            id={id}
            className={`inline-flex items-baseline gap-1 ${wrapperClassName}`}
        >
            {prefix && <span className={currencyClassName}>{prefix}</span>}
            <span className={priceClassName}>{priceString}</span>
            {suffix && <span className={currencyClassName}>{suffix}</span>}
        </span>
    );
};

const ProductCardDefaultInner = ({
    product,
    href,
    // t,
    level,
    refreshfav,
    variantId,
    priorityImage,
    shopCurrency,
    favoriteEnable,
}: CollectionListItemProps) => {
    // Equalize heights of the two dynamic blocks across all product cards so layout rows align.
    useEffect(() => {
        const syncHeights = () => {
            const infoBlocks = Array.from(
                document.querySelectorAll<HTMLDivElement>(
                    '#product-card-default-info .product-item-next-step-info',
                ),
            );
            const normalBlocks = Array.from(
                document.querySelectorAll<HTMLDivElement>(
                    '#product-card-default-info .product-item-next-step-info-normal',
                ),
            );
            // Reset heights to natural first
            infoBlocks.forEach((el) => (el.style.height = 'auto'));
            normalBlocks.forEach((el) => (el.style.height = 'auto'));
            const maxInfo = infoBlocks.reduce(
                (m, el) => Math.max(m, el.offsetHeight),
                0,
            );
            const maxNormal = normalBlocks.reduce(
                (m, el) => Math.max(m, el.offsetHeight),
                0,
            );
            infoBlocks.forEach((el) => (el.style.height = maxInfo + 'px'));
            normalBlocks.forEach((el) => (el.style.height = maxNormal + 'px'));
        };
        // Run after mount & on images load slight delay
        const timeout = setTimeout(syncHeights, 50);
        window.addEventListener('resize', syncHeights);
        return () => {
            clearTimeout(timeout);
            window.removeEventListener('resize', syncHeights);
        };
    }, []);
    const { t } = useTranslation();
    let variant =
        product.productVariant.length > 0 ? product.productVariant[0] : null;
    if (variantId) {
        const variantsFilter = product.productVariant.filter(
            (x) => x.id === variantId,
        );
        if (variantsFilter.length > 0) {
            variant = variantsFilter[0];
        }
    }
    const title = product.attributes?.displayName ?? '';
    let cv: number | null;
    let qv: number | null;
    if (level === 'BP_VVIP') {
        cv = variant ? variant.CV_VVIP : null;
        qv = variant ? variant.QV_VVIP : null;
    } else if (level === 'BP_VIP') {
        cv = variant ? variant.CV_VIP : null;
        qv = variant ? variant.QV_VIP : null;
    } else {
        cv = variant ? variant.CV : null;
        qv = variant ? variant.QV : null;
    }

    const promotionDiscountedPrice = variant
        ? getPromotionDiscountedPrice(variant)
        : ({} as any);
    const rawStepTiers = promotionDiscountedPrice.STEP_PRICE?.value; // StepPriceValue[]
    // For collection list we assume quantity in cart = 0 (preview). So currentStep will be undefined and nextStep first tier.
    const stepSelection: StepPriceType | null = rawStepTiers
        ? (getBestPriceByType(rawStepTiers, 'STEP_PRICE', 0) as StepPriceType)
        : null;
    const nextStep = stepSelection?.nextStep;

    // Apply Discount Promotion price
    variant = applyDiscountPromotion(promotionDiscountedPrice, variant);

    // Effective special price for display. Step price is a special price: apply it
    // only when it beats the existing special/normal price. Kept in locals (we do NOT
    // mutate variant) so re-renders never compound the discount.
    let displayCompareFull = variant?.comparePriceFull ?? null;
    let displayCompareTrial = variant?.comparePriceTrial ?? null;
    if (variant && nextStep) {
        const stepFull = nextStep.price['GUEST']?.price;
        const stepTrial = nextStep.price['BP']?.price;
        if (stepFull != null)
            displayCompareFull = Math.min(
                stepFull,
                displayCompareFull ?? variant.priceFull,
            );
        if (stepTrial != null)
            displayCompareTrial = Math.min(
                stepTrial,
                displayCompareTrial ?? variant.priceTrial,
            );
    }

    const trialNormalPrice = variant ? variant.priceTrial : null;
    const normalPrice = variant ? variant.priceFull : null;
    // Base the step discount is measured from: special price when set & lower
    // (same base as getPromotionDiscountedPrice), else the list price.
    const stepNormalPrice = !variant
        ? 0
        : level === 'GUEST'
          ? variant.comparePriceFull != null &&
            variant.comparePriceFull < variant.priceFull
              ? variant.comparePriceFull
              : variant.priceFull
          : variant.comparePriceTrial != null &&
              variant.comparePriceTrial < variant.priceTrial
            ? variant.comparePriceTrial
            : variant.priceTrial;
    const isTrialHaveSpecialPrice =
        variant && displayCompareTrial != null
            ? displayCompareTrial < variant.priceTrial
            : false;
    const isFullHaveSpecialPrice =
        variant && displayCompareFull != null
            ? displayCompareFull < variant.priceFull
            : false;
    const trialPrice = isTrialHaveSpecialPrice
        ? displayCompareTrial
        : variant
          ? variant.priceTrial
          : null;
    const price = isFullHaveSpecialPrice
        ? displayCompareFull
        : variant
          ? variant.priceFull
          : null;

    var imageUrl = product.featuredImageUrl
        ? product.featuredImageUrl
        : (variant ? variant.featuredImageUrl : '') || '';

    const imageIndex = product.imagesUrl.findIndex(
        (image) => image === imageUrl,
    );
    const imageAlt = product.imagesAlt[imageIndex] || `product-${product.id}`;

    const colorStyle = useContext(ColorStyleContext);

    return (
        <div
            id={`product-card-default-style-${product.id}`}
            className="flex flex-col gap-6 justify-start items-start w-full h-full"
        >
            <div
                id="product-card-default-image-container"
                className="relative w-full flex justify-center items-center aspect-square min-h-240 sm:min-h-480"
                style={{ maxWidth: '100%' }}
            >
                <a
                    id={`product-card-default-link`}
                    href={href}
                    className="w-full h-full"
                >
                    <SmartImage
                        id={`product-item-img`}
                        src={imageUrl}
                        alt={imageAlt || 'Product image'}
                        width={480}
                        height={480}
                        className="h-full w-full object-contain bg-white rounded-3xl"
                        loading={priorityImage ? 'eager' : 'lazy'}
                        preferResize={true}
                    />
                </a>
                {/* <div className="p-1 bg-white rounded-full w-7 h-7 absolute top-2 right-2"> */}
                {/* <span className="material-symbols-outlined text-lg w-full text-center">favorite</span> */}
                {favoriteEnable && (
                    <CollectionListItemClient
                        productHandle={product.handle}
                        refresh={refreshfav}
                    />
                )}
                {/* </div> */}
            </div>
            <div
                id="product-card-default-info"
                className="flex flex-col gap-5 justify-between w-full h-full"
            >
                <div id={`product-item-title-container`}>
                    <a
                        id={`product-item-link`}
                        href={href}
                        className="font-semibold text-lg leading-8 hover:opacity-50 dynamic-color"
                        style={{
                            color: colorStyle?.text || '#3d3d3d',
                        }}
                    >
                        <span
                            id={`product-item-title`}
                            className="line-clamp-3"
                        >
                            {title}
                        </span>
                    </a>
                </div>
                <div
                    id="product-card-default-info-bottom"
                    className="flex flex-col gap-5 justify-end w-full h-full"
                >
                    <ProductTagRenderer tags={product.tags} iconOnly />
                    {
                        // if price contain key of "STEP_PRICE"
                        nextStep && variant?.show_price ? (
                            <div
                                id={`product-item-next-step`}
                                className="w-full flex flex-col"
                            >
                                <div
                                    id={`product-item-next-step-info`}
                                    className="product-item-next-step-info w-full text-[#ED1C24] p-[10px] bg-[#FFF200] rounded-t-lg"
                                >
                                    <div
                                        id={`product-item-next-step-info-text`}
                                        className="w-full flex flex-col items-start justify-end"
                                    >
                                        <span
                                            id={`product-item-next-step-info-quantity`}
                                            className="text-sm"
                                        >
                                            {t('promotion.step_price_steps', {
                                                step: nextStep.step,
                                            })}
                                        </span>
                                        <span
                                            id={`product-item-next-step-info-price`}
                                            className="w-full flex flex-col gap-2 items-start font-semibold xl:text-3xl lg:text-2xl text-xl leading-8"
                                        >
                                            {nextStep.price[level] !==
                                                undefined && (
                                                <CurrencyPrice
                                                    id={`product-item-next-step-info-price-value`}
                                                    priceString={toPriceString(
                                                        nextStep.price[level]!
                                                            .price,
                                                    )}
                                                    shopCurrency={shopCurrency}
                                                    wrapperClassName="font-semibold"
                                                    priceClassName="xl:text-[30px] lg:text-[24px] text-[20px] leading-8"
                                                    currencyClassName="text-sm"
                                                />
                                            )}
                                            <div className="w-full flex flex-row gap-2 justify-between">
                                                <span
                                                    id={`product-item-next-step-info-per-item`}
                                                    className="text-xs xl:leading-5 leading-6"
                                                >
                                                    {t('promotion.per_item', {
                                                        step: nextStep.step,
                                                    })}
                                                </span>
                                            </div>
                                        </span>
                                    </div>
                                </div>
                                <div className="product-item-next-step-info-normal rounded-b-lg w-full bg-[#E21E06] text-white p-[10px]">
                                    <div
                                        id={`product-item-next-step-info-normal`}
                                        className="w-full flex flex-wrap flex-row  gap-px justify-between items-start"
                                    >
                                        <span
                                            id={`product-item-next-step-info-normal-price`}
                                            className="flex flex-col gap-px font-normal text-white"
                                        >
                                            <span style={{ fontSize: '10px' }}>
                                                {t('product.normal')}
                                            </span>
                                            <span className="flex flex-row gap-2 items-end">
                                                <span className="line-through xl:text-base lg:text-sm text-xs">
                                                    {formatCurrencyFormat(
                                                        toPriceString(
                                                            stepNormalPrice,
                                                        ),
                                                        shopCurrency,
                                                    )}
                                                </span>
                                                <span
                                                    id={`product-item-next-step-info-per-item`}
                                                    style={{ fontSize: '10px' }}
                                                >
                                                    {t('promotion.per_item', {
                                                        step: nextStep.step,
                                                    })}
                                                </span>
                                            </span>
                                        </span>
                                        <span
                                            id={`product-item-next-step-info-normal-price-percentage`}
                                            className="text-xs font-semibold  self-end"
                                        >
                                            {t('promotion.saved_percentage', {
                                                percentage: Math.floor(
                                                    nextStep.price[level]
                                                        ?.percentage || 0,
                                                ),
                                            })}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ) : null
                    }
                    {variant?.show_price && !nextStep && (
                        <div>
                            <div
                                id={`product-item-price`}
                                className="flex flex-col w-full"
                            >
                                {/* BP Price */}
                                {level === 'BP' && (
                                    <div
                                        id={`product-item-bp-price`}
                                        className="flex flex-row items-center flex-wrap gap-2 min-h-8"
                                    >
                                        {trialPrice !== null &&
                                            trialPrice !== undefined && (
                                                <span
                                                    id={`product-item-price-special`}
                                                    className="font-semibold text-xl leading-8"
                                                    style={{
                                                        color:
                                                            colorStyle?.text ||
                                                            '#3d3d3d',
                                                    }}
                                                >
                                                    {formatCurrencyFormat(
                                                        toPriceString(
                                                            trialPrice,
                                                        ),
                                                        shopCurrency,
                                                    )}
                                                </span>
                                            )}
                                        {isTrialHaveSpecialPrice &&
                                            trialNormalPrice && (
                                                <span
                                                    id={`product-item-price-normal`}
                                                    className="line-through font-normal text-base leading-8"
                                                    style={{
                                                        color:
                                                            colorStyle?.text ||
                                                            '#3d3d3d',
                                                        opacity: 0.5,
                                                    }}
                                                >
                                                    {formatCurrencyFormat(
                                                        toPriceString(
                                                            trialNormalPrice,
                                                        ),
                                                        shopCurrency,
                                                    )}
                                                </span>
                                            )}
                                    </div>
                                )}
                                {/* Normal Price */}
                                {level === 'NORMAL' ||
                                    (level === 'GUEST' && (
                                        <div
                                            id={`product-item-normal-price`}
                                            className="flex flex-row items-center flex-wrap gap-2 min-h-8"
                                        >
                                            {price !== null &&
                                                price !== undefined && (
                                                    <span
                                                        id={`product-item-price-special`}
                                                        className="font-semibold text-xl leading-8"
                                                        style={{
                                                            color:
                                                                colorStyle?.text ||
                                                                '#3d3d3d',
                                                        }}
                                                    >
                                                        {formatCurrencyFormat(
                                                            toPriceString(
                                                                price,
                                                            ),
                                                            shopCurrency,
                                                        )}
                                                    </span>
                                                )}
                                            {isFullHaveSpecialPrice &&
                                                normalPrice && (
                                                    <span
                                                        id={`product-item-price-normal`}
                                                        className="line-through font-normal text-base leading-8"
                                                        style={{
                                                            color:
                                                                colorStyle?.text ||
                                                                '#3d3d3d',
                                                            opacity: 0.5,
                                                        }}
                                                    >
                                                        {formatCurrencyFormat(
                                                            toPriceString(
                                                                normalPrice,
                                                            ),
                                                            shopCurrency,
                                                        )}
                                                    </span>
                                                )}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Comparator: re-render only if key props or core product pricing/tag info change.
// (Conservative: if unsure, return false to allow update.)
const areEqual = (
    prev: CollectionListItemProps,
    next: CollectionListItemProps,
) => {
    if (prev === next) return true;
    if (prev.href !== next.href) return false;
    if (prev.level !== next.level) return false;
    if (prev.variantId !== next.variantId) return false;
    if (prev.priorityImage !== next.priorityImage) return false;
    if (prev.shopCurrency !== next.shopCurrency) return false;
    if (prev.refreshfav !== next.refreshfav) return false;
    const pp = prev.product;
    const np = next.product;
    if (pp === np) return true; // same reference
    if (!pp || !np) return false;
    if (pp.id !== np.id) return false;
    // Compare basic displayed fields
    if (pp.attributes?.displayName !== np.attributes?.displayName) return false;
    // Compare variant counts & first variant core pricing (most cards show first variant)
    const pVar0 = pp.productVariant?.[0];
    const nVar0 = np.productVariant?.[0];
    if (!!pVar0 !== !!nVar0) return false;
    if (pVar0 && nVar0) {
        if (pVar0.id !== nVar0.id) return false;
        if (pVar0.priceFull !== nVar0.priceFull) return false;
        if (pVar0.comparePriceFull !== nVar0.comparePriceFull) return false;
        if (pVar0.priceTrial !== nVar0.priceTrial) return false;
        if (pVar0.comparePriceTrial !== nVar0.comparePriceTrial) return false;
    }
    // Compare tag ids (order-insensitive) length + ids set
    const pTags = pp.tags || [];
    const nTags = np.tags || [];
    if (pTags.length !== nTags.length) return false;
    for (let i = 0; i < pTags.length; i++) {
        if (pTags[i].id !== nTags[i].id) return false; // assumes stable ordering
    }
    return true; // no significant changes detected
};

const Loading = () => {
    return (
        <div className="flex flex-col gap-6 justify-start items-start w-full h-full animate-pulse">
            <div
                className="relative w-full flex justify-center items-center aspect-square min-h-240 sm:min-h-480"
                style={{ maxWidth: '100%' }}
            >
                <div className="h-full w-full bg-gray-200 rounded-3xl" />
            </div>
            <div className="flex flex-col gap-5 justify-between w-full h-full">
                <div className="h-12 w-full bg-gray-200 rounded" />
                <div className="flex gap-2 items-center">
                    <div className="h-6 w-6 bg-gray-200 rounded-full" />
                    <div className="h-6 w-6 bg-gray-200 rounded-full" />
                    <div className="h-6 w-6 bg-gray-200 rounded-full" />
                </div>
                <div className="h-6 w-1/4 bg-gray-200 rounded" />
                <div className="h-6 w-1/3 bg-gray-200 rounded" />
            </div>
        </div>
    );
};

const ProductCardDefault = memo(ProductCardDefaultInner, areEqual);

export default dynamic(() => Promise.resolve(ProductCardDefault), {
    ssr: false,
    loading: Loading,
});
